import { v3 } from "backoffice-api"
import { formatNumber } from "format"
import { useQueryParam } from "hooks"
import {
  type Leaderboard as LeaderboardApi,
  leaderboard,
} from "leaderboard-api"
import { times } from "lodash-es"
import { useTranslation } from "react-i18next"
import { QueryBoundary, WhenVisible } from "utility-components"
import { type Custom, custom } from "../../../bonzai/bonzai"
import { getNextPageParamV1 } from "../../../bonzai/getNextPageParamV1"
import { useFormatUser } from "../../../bonzai/useFormatUser"
import { getProductLink } from "../../../dataUtilities/productDataUtilities"
import { usePickText } from "../../../i18n/usePickText"
import { ConfigError } from "../../../tools/ConfigError"
import { useIsFeatureFlagEnabled } from "../../../tools/useIsFeatureFlagEnabled"
import { LeaderboardEmpty } from "../LeaderboardEmpty"
import { LeaderboardViewCompact } from "./LeaderboardViewCompact"

type LeaderboardScope = Custom["getLeaderboardScopes"][number]

const PRODUCTS_PER_PAGE = 6

export const LeaderboardViewCompactLoader = () => (
  <LeaderboardViewCompact>
    <QueryBoundary fallback={<LeaderboardSkeletons />}>
      <Load />
    </QueryBoundary>
  </LeaderboardViewCompact>
)

const Load = () => {
  const { scopes, scope, setScope } = useLeaderboardScopes()

  return (
    <>
      <Scopes scopes={scopes} value={scope} onChange={setScope} />
      <QueryBoundary fallback={<LeaderboardSkeletons />}>
        <LeaderboardPages scope={scope} />
      </QueryBoundary>
    </>
  )
}

type ScopesProps = {
  value: string
  onChange: (value: string) => void
  scopes: LeaderboardScope[]
}
const Scopes = ({ scopes, value, onChange }: ScopesProps) => {
  const scopeElements = scopes.map((scope) => (
    <LeaderboardViewCompact.Scope value={scope.value} key={scope.value}>
      {scope.name}
    </LeaderboardViewCompact.Scope>
  ))

  return (
    <LeaderboardViewCompact.ScopeSelect value={value} onChange={onChange}>
      {scopeElements}
    </LeaderboardViewCompact.ScopeSelect>
  )
}

type LeaderboardPagesProps = {
  scope: string
}
const LeaderboardPages = ({ scope }: LeaderboardPagesProps) => {
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useLeaderboardsData()

  const productIds = data.pages.flatMap((page) => page.data)
  const highScoresResults = useHighScores(productIds, scope)

  const isLoadingHighScores = highScoresResults.some(
    (result) => result.isPending
  )

  const leaderboards = productIds.flatMap((productId, index) => {
    const highScores = highScoresResults.at(index)?.data
    if (highScores === undefined || highScores.entries.length === 0) return []
    return [{ productId, highScores }]
  })

  const firstError = highScoresResults.find((result) => result.error)?.error

  // <WhenVisible> pages past all-empty pages, so nothing left here means
  // nothing exists. An error is different: we never learned whether anyone
  // played, so let QueryBoundary report it rather than claim "no players".
  if (leaderboards.length === 0 && !isLoadingHighScores && !hasNextPage) {
    if (firstError) throw firstError

    return <LeaderboardEmpty />
  }

  const leaderboardElements = leaderboards.map(({ productId, highScores }) => (
    <QueryBoundary
      key={productId}
      fallback={<LeaderboardViewCompact.Skeleton />}
    >
      <Leaderboard productId={productId} highScores={highScores} />
    </QueryBoundary>
  ))

  return (
    <>
      {leaderboardElements}
      {(isLoadingHighScores || isFetchingNextPage) && <LeaderboardSkeletons />}

      <WhenVisible
        key={`${scope}-${productIds.length}`}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />
    </>
  )
}

type LeaderboardProps = {
  productId: number
  highScores: LeaderboardApi["getHighScores"]
}
const Leaderboard = ({ productId, highScores }: LeaderboardProps) => {
  const { t, i18n } = useTranslation()
  const formatUser = useFormatUser()
  const pickText = usePickText()

  const product = v3.getProduct.useSuspenseQuery([productId], {
    select: (res) => res.data,
  })

  const showMyEntry = highScores.my_entry.value > 0

  const podiumEntries = highScores.entries.slice(0, 3).map((entry, index) => (
    <LeaderboardViewCompact.EntryPodium
      // biome-ignore lint/suspicious/noArrayIndexKey: entries never reorder within a rendered board, and rank is not unique when players tie
      key={`podium-${index}`}
      rank={entry.rank}
      avatar={entry.user.image}
      avatarTitle={entry.user.image_title["en-US"]}
      name={formatUser(entry.user)}
      score={formatNumber(entry.value, i18n.language)}
      isFirst={index === 0}
    />
  ))

  const entries = highScores.entries.slice(3).map((entry, index) => (
    <LeaderboardViewCompact.Entry
      // biome-ignore lint/suspicious/noArrayIndexKey: entries never reorder within a rendered board, and rank is not unique when players tie
      key={`entry-${index}`}
      rank={entry.rank}
      avatar={entry.user.image}
      avatarTitle={entry.user.image_title["en-US"]}
      name={formatUser(entry.user)}
      score={formatNumber(entry.value, i18n.language)}
      isMe={false}
    />
  ))

  return (
    <LeaderboardViewCompact.Leaderboard>
      <LeaderboardViewCompact.Header
        link={{
          to: getProductLink(product.id, product.product_type.identifier),
        }}
        title={pickText(product.title)}
        linkText={t("leaderboard.SEE_ALL")}
      />
      <LeaderboardViewCompact.Podium>
        {podiumEntries}
      </LeaderboardViewCompact.Podium>
      {entries}
      {showMyEntry && (
        <LeaderboardViewCompact.Entry
          rank={highScores.my_entry.rank}
          avatar={highScores.my_entry.user.image}
          avatarTitle={highScores.my_entry.user.image_title["en-US"]}
          name={formatUser(highScores.my_entry.user)}
          score={formatNumber(highScores.my_entry.value, i18n.language)}
          isMe={true}
        />
      )}
    </LeaderboardViewCompact.Leaderboard>
  )
}

const LeaderboardSkeletons = () => (
  <>
    {times(PRODUCTS_PER_PAGE, () => (
      <LeaderboardViewCompact.Skeleton />
    ))}
  </>
)

const useLeaderboardScopes = () => {
  const { getIsPortalFlagEnabled } = useIsFeatureFlagEnabled()

  const isGlobalLeaderboardEnabled = !getIsPortalFlagEnabled(
    "disable_global_leaderboard"
  )

  const scopes = custom.getLeaderboardScopes.useSuspenseQuery([
    isGlobalLeaderboardEnabled,
  ])
  const firstScope = scopes[0]

  if (firstScope === undefined) {
    throw new ConfigError("No leaderboard scopes")
  }

  const [scope = firstScope.value, setScope] = useQueryParam("scope")

  return { scopes, scope, setScope }
}

const useLeaderboardsData = () => {
  return custom.getPlayableProductIds.useSuspenseInfiniteQuery(
    [{ per_page: PRODUCTS_PER_PAGE }],
    {
      initialPageParam: { page: 1 },
      getNextPageParam: getNextPageParamV1,
    }
  )
}

// Deliberately does not throw per query: one failing product should cost one
// board, not the whole list. <LeaderboardPages> throws only if an error left it
// with nothing to render at all.
// TODO [ENGMAN-306] this fan-out goes away once leaderboards come from the BFF
// field, which pages server-side and reports emptiness in pageInfo.
const useHighScores = (productIds: number[], scope: string) => {
  return leaderboard.getHighScores.useQueries(
    productIds.map((product_id) => [{ product_id, scope, length: 6 }] as const)
  )
}
